import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Chip
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  PersonAdd as SignupIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import AuthApiService from '../../services/authApi';
import { SignupFormData } from '../../types/auth';
import { errorField } from '../../lib/http';

interface SignupFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
}

const SignupForm: React.FC<SignupFormProps> = ({ onSuccess, onSwitchToLogin }) => {
  const [formData, setFormData] = useState<SignupFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    invitationCode: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [codeValidation, setCodeValidation] = useState<{
    validated: boolean;
    codeType?: string;
  }>({ validated: false });

  const handleChange = (field: keyof SignupFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value });
    setError(null);
  };

  const validateCode = async () => {
    if (!formData.invitationCode) {
      setError('Veuillez entrer un code d\'invitation');
      return;
    }

    setLoading(true);
    try {
      const response = await AuthApiService.validateInvitationCode(formData.invitationCode);
      if (response.success && response.valid) {
        setCodeValidation({
          validated: true,
          codeType: response.codeType
        });
        setError(null);
      } else {
        setError(response.error || 'Code d\'invitation invalide');
        setCodeValidation({ validated: false });
      }
    } catch (err: unknown) {
      setError(errorField(err, 'message') || 'Erreur lors de la validation du code');
      setCodeValidation({ validated: false });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation frontend
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (!codeValidation.validated) {
      setError('Veuillez valider votre code d\'invitation d\'abord');
      return;
    }

    setLoading(true);

    try {
      const response = await AuthApiService.signup({
        email: formData.email,
        password: formData.password,
        displayName: formData.displayName,
        invitationCode: formData.invitationCode
      });

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess?.();
        }, 2000);
      } else {
        setError(response.error || 'Erreur lors de l\'inscription');
      }
    } catch (err: unknown) {
      setError(errorField(err, 'message') || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box textAlign="center" py={4}>
        <CheckIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Inscription réussie!
        </Typography>
        <Typography color="textSecondary">
          Vous pouvez maintenant vous connecter avec votre email et mot de passe.
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TextField
        label="Nom complet"
        value={formData.displayName}
        onChange={handleChange('displayName')}
        fullWidth
        required
        sx={{ mb: 2 }}
        autoComplete="name"
      />

      <TextField
        label="Email"
        type="email"
        value={formData.email}
        onChange={handleChange('email')}
        fullWidth
        required
        sx={{ mb: 2 }}
        autoComplete="email"
      />

      <TextField
        label="Mot de passe"
        type={showPassword ? 'text' : 'password'}
        value={formData.password}
        onChange={handleChange('password')}
        fullWidth
        required
        sx={{ mb: 2 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />

      <TextField
        label="Confirmer le mot de passe"
        type={showConfirmPassword ? 'text' : 'password'}
        value={formData.confirmPassword}
        onChange={handleChange('confirmPassword')}
        fullWidth
        required
        sx={{ mb: 2 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />

      <Box sx={{ mb: 3 }}>
        <Box display="flex" gap={1}>
          <TextField
            label="Code d'invitation"
            value={formData.invitationCode}
            onChange={handleChange('invitationCode')}
            fullWidth
            required
            disabled={codeValidation.validated}
            InputProps={{
              endAdornment: codeValidation.validated && (
                <InputAdornment position="end">
                  <CheckIcon color="success" />
                </InputAdornment>
              )
            }}
          />
          {!codeValidation.validated && (
            <Button
              variant="outlined"
              onClick={validateCode}
              disabled={loading || !formData.invitationCode}
            >
              Valider
            </Button>
          )}
        </Box>
        {codeValidation.validated && codeValidation.codeType && (
          <Chip
            label={`Rôle: ${codeValidation.codeType}`}
            color="success"
            size="small"
            sx={{ mt: 1 }}
          />
        )}
      </Box>

      <Button
        type="submit"
        variant="contained"
        startIcon={loading ? <CircularProgress size={20} /> : <SignupIcon />}
        disabled={loading || !codeValidation.validated}
        size="large"
        fullWidth
        sx={{ mb: 2 }}
      >
        S'inscrire
      </Button>

      <Button
        variant="text"
        onClick={onSwitchToLogin}
        fullWidth
      >
        Déjà un compte? Se connecter
      </Button>
    </Box>
  );
};

export default SignupForm;
